import type { ICategoriaProduto } from "interfaces/loja";

export function flattenTree(categorias: ICategoriaProduto[]): ICategoriaProduto[] {
  const result: ICategoriaProduto[] = [];
  categorias.forEach((cat) => {
    result.push(cat);
    if (cat.subcategorias?.length) {
      result.push(...flattenTree(cat.subcategorias));
    }
  });
  return result;
}

export function getDescendantIds(categoria: ICategoriaProduto): number[] {
  const ids: number[] = [];
  categoria.subcategorias?.forEach((sub) => {
    ids.push(sub.id, ...getDescendantIds(sub));
  });
  return ids;
}

export function collectAllIds(categorias: ICategoriaProduto[]): number[] {
  return flattenTree(categorias).map((c) => c.id);
}

export function maxDepth(categorias: ICategoriaProduto[], level = 1): number {
  if (!categorias.length) return 0;
  return Math.max(
    ...categorias.map((c) =>
      c.subcategorias?.length
        ? maxDepth(c.subcategorias, level + 1)
        : level,
    ),
  );
}

export function filterTree(
  categorias: ICategoriaProduto[],
  term: string,
): ICategoriaProduto[] {
  const t = term.trim().toLowerCase();
  if (!t) return categorias;

  function walk(nodes: ICategoriaProduto[]): ICategoriaProduto[] {
    return nodes.reduce<ICategoriaProduto[]>((acc, node) => {
      const selfMatch =
        node.nome.toLowerCase().includes(t) ||
        node.slug.toLowerCase().includes(t) ||
        (node.descricao?.toLowerCase().includes(t) ?? false);

      if (selfMatch) {
        acc.push(node);
        return acc;
      }

      const filteredSubs = node.subcategorias?.length ? walk(node.subcategorias) : [];
      if (filteredSubs.length > 0) {
        acc.push({ ...node, subcategorias: filteredSubs });
      }
      return acc;
    }, []);
  }

  return walk(categorias);
}

export function getParentCategory(
  flat: ICategoriaProduto[],
  parentId: number | null | undefined,
): ICategoriaProduto | null {
  if (!parentId) return null;
  return flat.find((cat) => cat.id === parentId) ?? null;
}

export function computeStats(tree: ICategoriaProduto[]) {
  const flat = flattenTree(tree);
  const raiz = tree.length;
  const comFilhos = flat.filter((c) => (c.subcategorias?.length ?? 0) > 0).length;
  const profundidade = maxDepth(tree);
  return { total: flat.length, raiz, comFilhos, profundidade };
}

export function highlightMatch(text: string, term: string): boolean {
  if (!term.trim()) return false;
  return text.toLowerCase().includes(term.trim().toLowerCase());
}
